import { useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import { Headset, Menu, X } from 'lucide-react';

const viewLabels = {
  '/': 'Dashboard', '/my-sla': 'My SLA Dashboard', '/incidents': 'Incidents',
  '/service-requests': 'Service Requests', '/change-requests': 'Change Requests',
  '/new-request': 'New Request', '/sla-policies': 'SLA Policies',
  '/breach-report': 'SLA Breach Report', '/approval-workflows': 'Approval Workflows',
  '/shift-calendar': 'Shift Calendar', '/teams': 'Apps & Teams Mapping',
  '/escalation': 'Escalation Matrix', '/app-owners': 'Applications / Systems',
  '/authentication': 'Authentication', '/user-roles': 'User Roles',
  '/system-config': 'System Config', '/knowledge-base': 'Knowledge Base',
  '/ticket': 'Ticket Detail', '/cmdb': 'CMDB', '/my-tickets': 'My Tickets',
  '/my-bucket': 'My Bucket', '/my-tickets-search': 'Search Tickets',
  '/profile': 'My Profile', '/categories': 'Categories',
};

export default function AppLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathBase = '/' + location.pathname.split('/')[1];
  const currentLabel = viewLabels[location.pathname] || viewLabels[pathBase] || 'Dashboard';

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile, slides in when open */}
      <div className={`
        fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-auto lg:flex-shrink-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger — visible on mobile */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 bg-sky-500 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0">T</div>
              <h2 className="text-sm font-medium text-slate-600 dark:text-slate-400 truncate hidden sm:block">
                TRIVONT SupportOne / <span className="text-slate-800 dark:text-white font-semibold">{currentLabel}</span>
              </h2>
              <h2 className="text-sm font-semibold text-slate-800 dark:text-white truncate sm:hidden">{currentLabel}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationBell />
            <span className="hidden sm:flex text-xs bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full border dark:border-slate-700 shadow-sm items-center gap-1.5">
              <Headset className="w-3.5 h-3.5 text-sky-500" />
              <span className="dark:text-white">TRIVONT IT</span>
            </span>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 p-4 sm:p-6" data-testid="main-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
